import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { scrollDownBySetRow } from './setDoneScroll';
import { useQueries, useMutation, useQueryClient } from 'react-query';
import { TrashIcon, PlusIcon } from '@heroicons/react/24/solid';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid';
import './Workout.css';
import { colorFromString, textColorForHsl, hslWithAlpha } from '../../utils/colorUtils';
import { DurationInput } from "../DurationInput";
import { isWorkoutEditable } from './../../utils/workoutUtils';
import ExerciseDetailModal from './ExerciseDetailModal';
import Input from '../Input';
import { useUnits } from '../../contexts/UnitsContext';
import { lbsToKg, kgToLbs, kmToMiles, milesToKm, formatWeight } from '../../utils/units';
import { calculateProjectedWorkoutVolume, calculateProjectedWorkoutScore, isVolumeRecordOccurrence } from '../../utils/volume';
import ExerciseCoachChat from './ExerciseCoachChat';
import WorkoutExerciseHistoryChart from './WorkoutExerciseHistoryChart';
import WorkoutProposalBubble from './WorkoutProposalBubble';
import ExerciseCoachSuggestions, { reconcileExerciseCoachSuggestion } from './ExerciseCoachSuggestions';
import { reconcilePersistedSupersetSetUpdate } from './supersetCoachCompletion';
import { coachFieldEffectClass, useDisplayedCoachAppliedFieldEffects } from './coachAppliedFieldEffect';
import { useNewSetRowEffects } from './newSetRowEffect';
import { beginCoachFlow, bindCoachFlowSubject, failCoachFlow, markCoachFlow } from '../../services/coachSequenceTiming';

// Match standalone set times, including offset-less local datetime values.
const parseSetDate = (str) => {
  if (!str) return null;
  const localMatch = typeof str === 'string' && str.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (localMatch) {
    const [, year, month, day, hours, minutes, seconds = '0'] = localMatch;
    const d = new Date(+year, +month - 1, +day, +hours, +minutes, +seconds);
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
};

/** Gold crown marking an exercise that set a personal record this session. */
function CrownBadge() {
  return (
    <svg className="superset-exercise-crown" viewBox="0 0 24 24" role="img" aria-label="Personal record">
      <path d="M3 18h18l-1.2-9-3.6 4-4.2-6.2-4.2 6.2-3.6-4z" />
    </svg>
  );
}

/**
 * SupersetExercise component renders multiple exercises in a superset with their sets zipped together.
 * Sets are interleaved: Exercise A Set 1, Exercise B Set 1, Exercise A Set 2, Exercise B Set 2, etc.
 * All exercises are forced to have the same number of sets.
 */
function SupersetExercise({
  workout, 
  workoutExercises, // Array of workout_exercise objects in the superset
  exercises, 
  backendService, 
  modalService, 
  refreshWorkout,
  onCoachSuggestionResolved,
  isOngoing,
  setOngoingExerciseId,
  navigationSelection = null,
  isTemplateMode = false,
  allowCompletion = false,
  readOnly = false,
  structureLocked = false,
  recommendations = {},
  isLoadingRecommendations = false,
  updatingExerciseIds = new Set(),
  updatedExerciseIds = new Set(),
  onSubmitMessage,
  onAnswerQuestion,
  onCompletedSetFieldChanged,
  isSelectionMode = false,
  selectionOrder,
  setDateFilter,
  coachImageUrl = null,
  coachEmoji = '🥋',
  showInlineCoach = true,
  proposalAnnotationsByExercise = {},
  onApproveProposal,
  busyStrategyProposalId = null,
  coachAddedSetEffects,
  onCoachAddedSetDisplayed,
  coachFieldEffects,
  onCoachFieldEffectDisplayed
}) {
  const queryClient = useQueryClient();
  const { weightUnit, distanceUnit } = useUnits();
  
  // Track local sets for each exercise
  const [localSetsMap, setLocalSetsMap] = useState({});
  const [isAddingSet, setIsAddingSet] = useState(false);
  const [imageSrcMap, setImageSrcMap] = useState({});
  // Which exercise inside the superset is currently focused (drives chat + history chart).
  const [selectedExerciseId, setSelectedExerciseId] = useState(null);
  // Bodyweight for the workout date (needed for projected/completed volume + score).
  const [bodyweight, setBodyweight] = useState(0);
  const [bodyweightKeyLoaded, setBodyweightKeyLoaded] = useState(null);

  // Initialize selected exercise to the first one in the superset.
  useEffect(() => {
    if (workoutExercises.length === 0) return;
    if (!selectedExerciseId || !workoutExercises.some(we => we._id === selectedExerciseId)) {
      setSelectedExerciseId(workoutExercises[0]._id);
    }
  }, [workoutExercises, selectedExerciseId]);

  // Tracks the last manual tab pick to suppress auto-advance briefly.
  const userPickAtRef = useRef(0);

  // A shortcut is an explicit member selection, just like picking its tab.
  // Keep the round-major auto-advance from immediately replacing that intent.
  useEffect(() => {
    if (!navigationSelection?.exerciseId
      || navigationSelection.request.id !== String(workout._id)
      || !workoutExercises.some(ex => ex._id === navigationSelection.exerciseId)) return;
    userPickAtRef.current = Date.now();
    setSelectedExerciseId(navigationSelection.exerciseId);
    // Apply once per explicit shortcut, not on ordinary set/parent refreshes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationSelection]);

  const supersetExerciseIds = useMemo(() => workoutExercises.map(we => String(we._id)), [workoutExercises]);
  const supersetChatExerciseId = supersetExerciseIds[0] || null;
  const unifiedRecommendation = supersetChatExerciseId
    ? (recommendations[supersetChatExerciseId] || Object.values(recommendations).find(Boolean) || null)
    : null;
  const isSupersetUpdating = supersetExerciseIds.some(id => updatingExerciseIds.has(id));

  // Fetch bodyweight for the workout date (needed for volume / score projections).
  useEffect(() => {
    if (readOnly) return;
    let cancelled = false;
    const fetchBodyweight = async () => {
      if (!workout?.created_at) return;
      const dayKey = new Date(workout.created_at).toISOString().slice(0, 10);
      if (dayKey === bodyweightKeyLoaded) return;
      try {
        const bw = await backendService.fetchBodyweight(workout.created_at);
        if (cancelled) return;
        setBodyweight(bw || 0);
        setBodyweightKeyLoaded(dayKey);
      } catch (err) {
        console.error('Failed to fetch bodyweight for superset workout', err);
      }
    };
    fetchBodyweight();
    return () => { cancelled = true; };
  }, [readOnly, workout?.created_at, backendService, bodyweightKeyLoaded]);

  // (Selected-exercise derived values + auto-advance effect live below,
  //  after `displaySetsMap` and `maxSets` are declared, to avoid TDZ errors.)

  // Fetch sets for all exercises in the superset using useQueries (proper hook usage)
  const exerciseQueries = useQueries(
    workoutExercises.map(we => ({
      queryKey: ['workoutSets', we._id],
      queryFn: () => backendService.fetchWorkoutSets(workout._id, we._id),
      enabled: !readOnly,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        setLocalSetsMap(prev => ({ ...prev, [we._id]: data }));
      }
    }))
  );

  // Fetch history per exercise so we can flag personal records (crowns) when
  // an exercise's session score beats every prior session.
  const historyQueries = useQueries(
    workoutExercises.map(we => ({
      queryKey: ['workoutExerciseHistory', we._id],
      queryFn: () => backendService.fetchWorkoutExerciseHistory(we._id),
      retry: (failureCount, error) => error?.response?.status !== 503 && failureCount < 3,
      enabled: !readOnly,
      refetchOnWindowFocus: false
    }))
  );

  // onSuccess above only runs when a fetch resolves. Exercises that were already
  // rendered on their own share the ['workoutSets', id] key, so their sets come
  // back from cache and never reach localSetsMap, which downstream code would
  // read as an exercise with no sets at all. Seed straight from the query.
  const setsFromQueries = useMemo(() => {
    const map = {};
    workoutExercises.forEach((we, idx) => {
      const data = exerciseQueries[idx]?.data;
      if (Array.isArray(data)) map[we._id] = data;
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workoutExercises, exerciseQueries.map(q => q.dataUpdatedAt).join('|')]);

  useEffect(() => {
    setLocalSetsMap(prev => {
      const missing = Object.keys(setsFromQueries).filter(id => !Array.isArray(prev[id]));
      if (missing.length === 0) return prev;
      const next = { ...prev };
      missing.forEach(id => { next[id] = setsFromQueries[id]; });
      return next;
    });
  }, [setsFromQueries]);

  // When displaying a multipart workout, filter sets by the target date
  const displaySetsMap = useMemo(() => {
    if (readOnly) return Object.fromEntries(workoutExercises.map(we => [we._id, Array.isArray(we.sets) ? we.sets : []]));
    if (!setDateFilter) return localSetsMap;
    const filtered = {};
    const getDateKey = (d) => { try { return new Date(d).toLocaleDateString('en-CA'); } catch { return ''; } };
    for (const [weId, sets] of Object.entries(localSetsMap)) {
      filtered[weId] = sets.filter(s =>
        s.complete && s.completed_at && getDateKey(s.completed_at) === setDateFilter
      );
    }
    return filtered;
  }, [readOnly, workoutExercises, localSetsMap, setDateFilter]);
  const displayedCoachFieldEffects = useDisplayedCoachAppliedFieldEffects({
    effects: coachFieldEffects,
    displayedExercises: workoutExercises.map(workoutExercise => ({
      ...workoutExercise,
      sets: displaySetsMap[workoutExercise._id] || [],
    })),
    onDisplayed: onCoachFieldEffectDisplayed,
  });

  // Fetch images for all exercises
  useEffect(() => {
    const fetchImages = async () => {
      const imageMap = {};
      for (const we of workoutExercises) {
        const selectedExercise = exercises.find(e => e._id === we.exercise_id);
        if (selectedExercise && selectedExercise.images && selectedExercise.images.length > 0) {
          try {
            const url = await backendService.fetchExerciseImage(selectedExercise.images[1] || selectedExercise.images[0]);
            imageMap[we._id] = url;
          } catch (error) {
            console.error('Failed to fetch image:', error);
          }
        }
      }
      setImageSrcMap(imageMap);
    };
    fetchImages();
  }, [workoutExercises, exercises, backendService]);

  const isLoading = !readOnly && exerciseQueries.some(query => query.isLoading);

  // Get exercise details for each workout exercise
  const exerciseDetailsMap = useMemo(() => {
    const map = {};
    workoutExercises.forEach(we => {
      map[we._id] = exercises.find(e => e._id === we.exercise_id);
    });
    return map;
  }, [workoutExercises, exercises]);
  const supersetExerciseNamesById = useMemo(() => Object.fromEntries(
    workoutExercises.map(we => [String(we._id), exerciseDetailsMap[we._id]?.name || we.name || 'Superset exercise'])
  ), [workoutExercises, exerciseDetailsMap]);

  // Get color for each exercise based on its name
  const exerciseColorsMap = useMemo(() => {
    const map = {};
    workoutExercises.forEach(we => {
      const exercise = exerciseDetailsMap[we._id];
      if (exercise) {
        const hsl = colorFromString(exercise.name);
        map[we._id] = {
          bg: hslWithAlpha(hsl, 0.3),
          border: hslWithAlpha(hsl, 0.6),
          text: textColorForHsl(hsl)
        };
      }
    });
    return map;
  }, [workoutExercises, exerciseDetailsMap]);

  // Which exercises in the superset set a personal record this session. Records
  // belong to an individual exercise, so they are surfaced per exercise rather
  // than as one card-wide treatment.
  const recordExerciseIds = useMemo(() => {
    const ids = new Set();
    workoutExercises.forEach((we, idx) => {
      const history = readOnly ? null : historyQueries[idx]?.data?.history;
      const meta = exerciseDetailsMap[we._id];
      const category = (meta?.category || 'strength').toLowerCase();
      if (isVolumeRecordOccurrence(history, category, workout)) {
        ids.add(we._id);
      }
    });
    return ids;
  }, [readOnly, workoutExercises, historyQueries, exerciseDetailsMap, workout]);

  // Only a clean sweep earns the full-card gold celebration.
  const allExercisesAreRecords = workoutExercises.length > 0
    && recordExerciseIds.size === workoutExercises.length;

  // Determine the maximum number of sets across all exercises
  const maxSets = useMemo(() => {
    let max = 0;
    workoutExercises.forEach(we => {
      const sets = displaySetsMap[we._id] || [];
      if (sets.length > max) max = sets.length;
    });
    return max;
  }, [workoutExercises, displaySetsMap]);

  // Check if all sets across all exercises are complete
  const allSetsComplete = useMemo(() => {
    if (isTemplateMode) return false;
    
    // Get all sets from all exercises
    let totalSets = 0;
    let completedSets = 0;
    
    workoutExercises.forEach(we => {
      const sets = displaySetsMap[we._id] || [];
      sets.forEach(set => {
        totalSets++;
        if (set.complete) completedSets++;
      });
    });
    
    return totalSets > 0 && totalSets === completedSets;
  }, [workoutExercises, displaySetsMap, isTemplateMode]);

  // ---- Selected-exercise derived values (placed after `displaySetsMap` /
  //      `maxSets` to avoid TDZ) ---------------------------------------
  const selectedExerciseMeta = selectedExerciseId
    ? exercises.find(e => e._id === workoutExercises.find(we => we._id === selectedExerciseId)?.exercise_id)
    : null;
  const selectedSets = useMemo(() => selectedExerciseId ? (displaySetsMap[selectedExerciseId] || []) : [], [selectedExerciseId, displaySetsMap]);

  const unknownOwnerLoad = readOnly && (!selectedExerciseMeta || Number(selectedExerciseMeta.bodyWeightLoadFactor) > 0 || selectedExerciseMeta.estimateCaloriesForVolume);
  const selectedProjected = useMemo(() => {
    if (unknownOwnerLoad || !selectedExerciseMeta || selectedSets.length === 0) {
      return { volume: null, score: null };
    }
    const allDone = selectedSets.every(s => s.complete);
    if (allDone) return { volume: null, score: null };
    const volume = calculateProjectedWorkoutVolume(selectedSets, selectedExerciseMeta, bodyweight || 0);
    const score = calculateProjectedWorkoutScore(selectedSets, selectedExerciseMeta, bodyweight || 0);
    return {
      volume: Number.isFinite(volume) && volume > 0 ? volume : null,
      score: Number.isFinite(score) && score > 0 ? score : null,
    };
  }, [selectedSets, selectedExerciseMeta, bodyweight, unknownOwnerLoad]);

  const selectedCompleted = useMemo(() => {
    if (unknownOwnerLoad || !selectedExerciseMeta) return { volume: null, score: null };
    const completedSets = selectedSets.filter(s => s.complete);
    if (completedSets.length === 0 || completedSets.length === selectedSets.length) {
      return { volume: null, score: null };
    }
    const volume = calculateProjectedWorkoutVolume(completedSets, selectedExerciseMeta, bodyweight || 0);
    const score = calculateProjectedWorkoutScore(completedSets, selectedExerciseMeta, bodyweight || 0);
    return {
      volume: Number.isFinite(volume) && volume > 0 ? volume : null,
      score: Number.isFinite(score) && score > 0 ? score : null,
    };
  }, [selectedSets, selectedExerciseMeta, bodyweight, unknownOwnerLoad]);

  const selectedAllSetsComplete = selectedSets.length > 0 && selectedSets.every(s => s.complete);
  const selectedCategory = (selectedExerciseMeta?.category || 'strength').toLowerCase();

  // Auto-advance the focused exercise to the one that owns the next incomplete
  // set (round-major scan). Honors a recent manual tab pick for 30s so user
  // selections aren't immediately overridden.
  useEffect(() => {
    if (!isOngoing) return;
    if (Date.now() - userPickAtRef.current < 30000) return;

    for (let setIndex = 0; setIndex < maxSets; setIndex++) {
      for (const we of workoutExercises) {
        const sets = displaySetsMap[we._id] || [];
        const set = sets[setIndex];
        if (set && !set.complete) {
          if (we._id !== selectedExerciseId) setSelectedExerciseId(we._id);
          return;
        }
      }
    }
  }, [displaySetsMap, workoutExercises, maxSets, isOngoing, selectedExerciseId]);

  // Get columns based on category (use first exercise's category for simplicity)
  const getColumnsForExercise = useCallback((exerciseId) => {
    const exercise = exercises.find(e => e._id === exerciseId);
    const category = exercise?.category || 'strength';
    
    let columns = [];
    if (category === 'sport') {
      columns = [
        { header: 'Calories', field: 'calories', type: 'number', min: 0 },
        { header: 'Duration', field: 'duration', type: 'duration', min: 0 },
      ];
    } else if (category === 'stretching') {
      columns = [
        { header: 'Duration', field: 'duration', type: 'duration', min: 0 },
      ];
    } else if (category === 'static') {
      // Hide weight for exercises that don't use external load (e.g., battling ropes)
      const externalLoadFactor = exercise?.externalLoadFactor ?? 1;
      if (externalLoadFactor === 0) {
        columns = [
          { header: 'Duration', field: 'duration', type: 'duration', min: 0 },
        ];
      } else {
        columns = [
          { header: 'Weight', field: 'weight', type: 'number' },
          { header: 'Duration', field: 'duration', type: 'duration', min: 0 },
        ];
      }
    } else if (category === 'cardio') {
      columns = [
        { header: 'Distance', field: 'distance', type: 'number', min: 0 },
        { header: 'Duration', field: 'duration', type: 'duration', min: 0 },
      ];
    } else {
      columns = [
        { header: 'Weight', field: 'weight', type: 'number' },
        { header: 'Reps', field: 'repetitions', type: 'number', min: 0, step: 1 },
      ];
    }

    if (isTemplateMode) {
      columns = columns.filter(col => col.field !== 'weight');
    }

    if (allowCompletion && !isTemplateMode) {
      columns.push({ header: 'Done', field: 'complete', type: 'checkbox' });
    }

    // Add unit labels
    columns = columns.map((col) => {
      if (col.field === 'weight') {
        return { ...col, header: `${col.header} (${weightUnit === 'lbs' ? 'lbs' : 'kg'})` };
      }
      if (col.field === 'distance') {
        return { ...col, header: `${col.header} (${distanceUnit === 'km' ? 'km' : 'mi'})` };
      }
      return col;
    });

    return columns;
  }, [exercises, isTemplateMode, allowCompletion, weightUnit, distanceUnit]);

  // A review belongs to acknowledged writes in this mounted authenticated scope,
  // not to an optimistic checkbox/field edit still waiting in the durable outbox.
  const reviewScopeRef = useRef(null);
  const reviewScopeKey = JSON.stringify([workout?._id, workout?.status, allowCompletion, workoutExercises.map(we => we._id), readOnly]);
  if (reviewScopeRef.current?.key !== reviewScopeKey || reviewScopeRef.current?.service !== backendService) {
    reviewScopeRef.current = { key: reviewScopeKey, service: backendService, pending: new Set(), version: 0, dirty: false };
  }
  const reviewMountedRef = useRef(true);
  useEffect(() => {
    reviewMountedRef.current = true;
    return () => { reviewMountedRef.current = false; };
  }, []);
  const settleCoachEdit = async (variables, succeeded) => {
    const { owner, token, needsReview, workoutExerciseId } = variables;
    if (!reviewMountedRef.current || reviewScopeRef.current !== owner) return;
    owner.pending.delete(token);
    owner.dirty ||= succeeded && needsReview;
    if (owner.pending.size || !owner.dirty) return;
    const version = owner.version;
    await reconcilePersistedSupersetSetUpdate({
      shouldReview: true,
      workoutExerciseId,
      refreshWorkout,
      requestCoachReview: onCompletedSetFieldChanged,
      isCurrent: () => reviewMountedRef.current && reviewScopeRef.current === owner && owner.version === version,
    });
    if (reviewMountedRef.current && reviewScopeRef.current === owner && owner.version === version) owner.dirty = false;
  };

  // Time-only corrections must not enter the optimistic completion/review path.
  const timeScopeRef = useRef(null);
  const timeScopeKey = JSON.stringify([reviewScopeKey, structureLocked, isOngoing, isTemplateMode, isSelectionMode]);
  if (timeScopeRef.current?.key !== timeScopeKey || timeScopeRef.current?.service !== backendService) {
    timeScopeRef.current = { key: timeScopeKey, service: backendService, requests: new Map() };
  }
  const saveSetTime = async (workoutExerciseId, setId, value, owner) => {
    const parsed = parseSetDate(value);
    if (!parsed || readOnly || structureLocked || isSelectionMode || isTemplateMode || !allowCompletion
      || (workout.status === 'ongoing' && !isOngoing) || timeScopeRef.current !== owner) return;
    const token = {};
    owner.requests.set(setId, token);
    const isCurrent = () => reviewMountedRef.current && timeScopeRef.current === owner && owner.requests.get(setId) === token;
    const completed_at = parsed.toISOString();
    try {
      await backendService.updateWorkoutSet(setId, { completed_at }, {
        workoutId: workout?._id,
        workoutExerciseId,
        workoutStatus: workout?.status,
        source: 'superset_exercise_completed_at_edit'
      });
      if (!isCurrent()) return;
      await queryClient.cancelQueries(['workoutSets', workoutExerciseId]);
      if (!isCurrent()) return;
      // Merge just this field into the latest rows, never an old render snapshot.
      const mergeTime = sets => (sets || []).map(s => s._id === setId && s.complete ? { ...s, completed_at } : s);
      setLocalSetsMap(prev => ({ ...prev, [workoutExerciseId]: mergeTime(prev[workoutExerciseId]) }));
      queryClient.setQueryData(['workoutSets', workoutExerciseId], mergeTime);
      if (typeof refreshWorkout === 'function') await refreshWorkout();
    } catch (err) {
      console.error('Error updating set time:', err);
    }
  };

  // Mutation for updating a set
  const updateMutation = useMutation(
    ({ workoutExerciseId, setId, updates }) => {
      // react-query runs this after onMutate, so admission is the mutation start, not the gesture.
      const coachFlow = updates?.complete ? beginCoachFlow('set_review') : null;
      bindCoachFlowSubject(coachFlow, String(workoutExerciseId || ''));
      markCoachFlow(coachFlow, 'save_start');
      return backendService.updateWorkoutSet(setId, updates, {
        workoutId: workout?._id,
        workoutExerciseId
      }).then(saved => { markCoachFlow(coachFlow, 'save_end'); return saved; }, error => { failCoachFlow(coachFlow); throw error; });
    },
    {
      onMutate: async ({ workoutExerciseId, setId, updates }) => {
        await queryClient.cancelQueries(['workoutSets', workoutExerciseId]);
        setLocalSetsMap(prev => ({
          ...prev,
          [workoutExerciseId]: (prev[workoutExerciseId] || []).map(set =>
            set._id === setId ? { ...set, ...updates } : set
          )
        }));
      },
      onSuccess: (data, variables) => settleCoachEdit(variables, true),
      onError: (err, variables) => {
        console.error('Error updating set:', err);
        return settleCoachEdit(variables, false);
      },
      onSettled: (data, err, variables) => {
        queryClient.invalidateQueries(['workoutSets', variables.workoutExerciseId]);
      }
    }
  );

  const newSetRows = useNewSetRowEffects([workout?._id, ...workoutExercises.map(we => we._id)],
    workoutExercises.map(we => ({ ...we, sets: displaySetsMap[we._id] || [] })), readOnly, coachAddedSetEffects, onCoachAddedSetDisplayed);

  const additionScope = JSON.stringify([workout?._id, ...workoutExercises.map(we => we._id), readOnly]);
  useEffect(() => { setIsAddingSet(false); }, [additionScope]);

  // Each successful member owns its canonical row, including partial rounds.
  const addMutation = useMutation(
    ({ workoutExerciseId, newSet }) =>
      backendService.addWorkoutSet({ ...newSet, activity_id: workout?._id, workout_exercise_id: workoutExerciseId }),
    {
      onMutate: async ({ workoutExerciseId }) => {
        const owner = newSetRows.capture();
        await queryClient.cancelQueries(['workoutSets', workoutExerciseId]);
        return { owner };
      },
      onSuccess: (data, { workoutExerciseId }, context) => {
        if (!newSetRows.isCurrent(context?.owner) || !data?._id) return;
        newSetRows.publish(workoutExerciseId, data._id, context.owner);
        setLocalSetsMap(prev => ({
          ...prev,
          [workoutExerciseId]: (prev[workoutExerciseId] || []).some(set => set._id === data._id)
            ? prev[workoutExerciseId] : [...(prev[workoutExerciseId] || []), data]
        }));
      },
      onError: err => { console.error('Error adding set:', err); },
      onSettled: (data, err, variables) => {
        queryClient.invalidateQueries(['workoutSets', variables.workoutExerciseId]);
      }
    }
  );

  // Mutation for removing a set
  const removeMutation = useMutation(
    ({ workoutExerciseId, setId }) =>
      backendService.removeWorkoutSet(setId, { workoutId: workout?._id, workoutExerciseId }),
    {
      onMutate: async ({ workoutExerciseId, setId }) => {
        await queryClient.cancelQueries(['workoutSets', workoutExerciseId]);
        setLocalSetsMap(prev => ({
          ...prev,
          [workoutExerciseId]: (prev[workoutExerciseId] || []).filter(set => set._id !== setId)
        }));
      },
      onError: (err) => {
        console.error('Error removing set:', err);
      },
      onSettled: (data, err, variables) => {
        queryClient.invalidateQueries(['workoutSets', variables.workoutExerciseId]);
      }
    }
  );

  const handleSetChange = (workoutExerciseId, setId, updates) => {
    if (readOnly) return;
    const existingSet = (localSetsMap[workoutExerciseId] || []).find(s => s._id === setId);
    // A completion transition supersedes any correction to the previous time.
    if (Object.prototype.hasOwnProperty.call(updates, 'complete')) timeScopeRef.current.requests.delete(setId);
    const owner = reviewScopeRef.current;
    const token = ++owner.version;
    owner.pending.add(token);
    updateMutation.mutate({
      workoutExerciseId,
      setId,
      updates,
      owner,
      token,
      needsReview: Object.prototype.hasOwnProperty.call(updates, 'complete') || existingSet?.complete === true,
    });
  };

  const handleAddSetToAll = async () => {
    if (readOnly || isAddingSet) return;
    const owner = newSetRows.capture();
    setIsAddingSet(true);
    try {
      // Add a set to all exercises in the superset to keep them synchronized
      for (const we of workoutExercises) {
        const existingSets = localSetsMap[we._id] || [];
        const lastSet = existingSets.length > 0 ? existingSets[existingSets.length - 1] : null;
      
        // Copy values from last set or use defaults
        const newSet = lastSet ? {
          weight: lastSet.weight,
          repetitions: lastSet.repetitions,
          duration: lastSet.duration,
          distance: lastSet.distance,
          calories: lastSet.calories,
          complete: false
        } : {
          weight: 0,
          repetitions: 0,
          duration: 0,
          distance: 0,
          calories: 0,
          complete: false
        };

        if (!newSetRows.isCurrent(owner)) break;
        await addMutation.mutateAsync({
          workoutExerciseId: we._id,
          newSet
        });
      }
    } catch (_) {
      // Stop the round on failure; earlier canonical successes remain visible.
    } finally {
      if (newSetRows.isCurrent(owner)) setIsAddingSet(false);
    }
  };

  const handleRemoveSetFromAll = async (setIndex) => {
    // Remove the set at the given index from all exercises
    for (const we of workoutExercises) {
      const sets = localSetsMap[we._id] || [];
      if (sets[setIndex]) {
        await removeMutation.mutateAsync({
          workoutExerciseId: we._id,
          setId: sets[setIndex]._id
        });
      }
    }
  };

  const openExerciseDetail = (exerciseId) => {
    modalService.open(ExerciseDetailModal, {
      exerciseId,
      backendService
    });
  };

  // For supersets, editable when workout is editable and either:
  // - it's a template
  // - workout is not ongoing (planning mode)
  // - any exercise in the superset is selected as ongoing
  const completionEditable = !readOnly && allowCompletion && !isTemplateMode && (
    workout.status !== 'ongoing' || isOngoing
  );
  const editable = !readOnly && !structureLocked && !isSelectionMode && isWorkoutEditable(workout) && (
    isTemplateMode ||
    workout.status !== 'ongoing' ||
    isOngoing
  );

  // Compute per-exercise first incomplete set index (for restricting clickable sets)
  const firstIncompleteByExercise = useMemo(() => {
    const map = {};
    workoutExercises.forEach(we => {
      const sets = displaySetsMap[we._id] || [];
      map[we._id] = sets.findIndex(s => !s.complete);
    });
    return map;
  }, [workoutExercises, displaySetsMap]);

  if (isLoading) {
    return (
      <div className="superset-exercise-parent loading">
        <div className="exercise-loading-container">
          <div className="exercise-loading-text">Loading Superset...</div>
        </div>
      </div>
    );
  }

  // Build zipped rows: for each set index, show all exercises' sets at that index
  const zippedRows = [];
  for (let setIndex = 0; setIndex < maxSets; setIndex++) {
    workoutExercises.forEach((we, exIndex) => {
      const sets = displaySetsMap[we._id] || [];
      const set = sets[setIndex];
      const exercise = exerciseDetailsMap[we._id];
      const columns = getColumnsForExercise(we.exercise_id);
      const colors = exerciseColorsMap[we._id] || {};
      
      const firstIncomplete = firstIncompleteByExercise[we._id];
      const lastCompleted = firstIncomplete === -1
        ? sets.length - 1
        : firstIncomplete - 1;
      const isCurrentSet = setIndex === firstIncomplete;
      const isLastCompleted = setIndex === lastCompleted;
      const isSetInteractive = isCurrentSet || isLastCompleted;

      zippedRows.push({
        key: `${we._id}-${set?._id || `empty-${setIndex}`}`,
        setIndex,
        exerciseIndex: exIndex,
        workoutExercise: we,
        exercise,
        set,
        columns,
        colors,
        isFirstInRound: exIndex === 0,
        isLastInRound: exIndex === workoutExercises.length - 1,
        isCurrentSet,
        isSetInteractive
      });
    });
  }
  const setColumns = workoutExercises.length > 0
    ? getColumnsForExercise(workoutExercises[0].exercise_id)
    : [];

  return (
    <>
    <div inert={isSelectionMode ? '' : undefined} className={`superset-exercise-parent ${isOngoing ? 'active' : ''} ${allSetsComplete ? 'all-complete' : ''} ${allSetsComplete && allExercisesAreRecords ? 'volume-record' : ''}`}>
      {/* Overlay layers for complete state - uses shared classes */}
      {allSetsComplete && (
        <>
          <div className={`complete-overlay-base ${allExercisesAreRecords ? 'complete-overlay-record-bg' : 'complete-overlay-green'}`}></div>
          {allExercisesAreRecords ? (
            <div className="complete-overlay-base complete-overlay-crowns"></div>
          ) : (
            <>
              <div className="complete-overlay-base superset-complete-checkmarks-source"></div>
              <div className="complete-overlay-base exercise-overlay-darken"></div>
            </>
          )}
        </>
      )}
      {/* One unified Superset Coach conversation remains mounted while the
          selected exercise drives only the per-exercise history chart. */}
      {isOngoing && selectedExerciseId && (
        <div className="superset-focused-panel">
          {!readOnly && showInlineCoach && (
            <ExerciseCoachChat
            workout={workout}
            workoutExerciseId={supersetChatExerciseId}
            workoutExerciseIds={supersetExerciseIds}
            exerciseNamesById={supersetExerciseNamesById}
            backendService={backendService}
            refreshWorkout={refreshWorkout}
            recommendation={unifiedRecommendation}
            isLoadingRecommendation={isLoadingRecommendations && !unifiedRecommendation}
            isUpdatingRecommendation={isSupersetUpdating}
            onSubmitMessage={onSubmitMessage}
            onAnswerQuestion={onAnswerQuestion}
            coachImageUrl={coachImageUrl}
            coachEmoji={coachEmoji}
            />
          )}
          <div className="exercise-history-chart-wrapper is-visible">
            <WorkoutExerciseHistoryChart
              workoutExerciseId={selectedExerciseId}
              category={selectedCategory}
              backendService={backendService}
              currentWorkoutId={workout?._id}
              currentWorkoutDate={workout?.created_at}
              projectedVolume={selectedProjected.volume}
              completedVolume={selectedCompleted.volume}
              projectedScore={selectedProjected.score}
              completedScore={selectedCompleted.score}
              allSetsComplete={selectedAllSetsComplete}
            />
          </div>
        </div>
      )}

      {/* Superset header / legend with all exercise names. Positioned right
          above the sets grid so it reads as the legend for the table below. */}
      <div className="superset-header">
        {workoutExercises.map((we, idx) => {
          const exercise = exerciseDetailsMap[we._id];
          const colors = exerciseColorsMap[we._id] || {};
          const isRecord = recordExerciseIds.has(we._id);
          const annotations = proposalAnnotationsByExercise[String(we._id)] || [];
          const selectTitle = () => {
            if (isOngoing) {
              userPickAtRef.current = Date.now();
              setSelectedExerciseId(we._id);
            } else if (readOnly) {
              setSelectedExerciseId(we._id);
              setOngoingExerciseId?.(we._id);
            } else {
              openExerciseDetail(we.exercise_id);
            }
          };
          return (
            <div className="superset-exercise-title-stack" key={we._id}>
              <div
                className={`superset-exercise-title${isOngoing && we._id === selectedExerciseId ? ' selected' : ''}${isRecord ? ' is-record' : ''}`}
                style={{
                  backgroundColor: colors.bg,
                  borderColor: isRecord ? 'rgba(255, 215, 0, 0.9)' : colors.border,
                  color: colors.text,
                  cursor: isOngoing ? 'pointer' : 'default'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  selectTitle();
                }}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' && e.key !== ' ') return;
                  e.preventDefault();
                  e.stopPropagation();
                  selectTitle();
                }}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  openExerciseDetail(we.exercise_id);
                }}
                role="button"
                tabIndex={0}
              >
                {isSelectionMode && selectionOrder?.has(we._id) ? (
                  <span className="superset-selection-order" data-exercise-id={we._id} aria-hidden="true">{selectionOrder.get(we._id)}</span>
                ) : <span className="superset-exercise-letter">{String.fromCharCode(65 + idx)}</span>}
                {isRecord && <CrownBadge />}
                <span className="superset-exercise-name">{exercise?.name || 'Unknown'}</span>
              </div>
              {annotations.map(annotation => (
                <WorkoutProposalBubble
                  key={`${we._id}:${annotation.proposalId}:${annotation.type}`}
                  annotation={annotation}
                  onApprove={onApproveProposal}
                  disabled={String(busyStrategyProposalId || '') === String(annotation.proposalId)}
                />
              ))}
              {!readOnly && workout.status === 'ongoing' && (
                <ExerciseCoachSuggestions
                  recommendation={recommendations[we._id]}
                  workoutId={workout._id}
                  workoutExerciseId={we._id}
                  workoutStatus={workout.status}
                  readOnly={readOnly}
                  editable={editable}
                  exerciseName={exercise?.name}
                  coachImageUrl={coachImageUrl}
                  coachEmoji={coachEmoji}
                  backendService={backendService}
                  onResolved={(resolution) => reconcileExerciseCoachSuggestion({
                    refreshWorkout: onCoachSuggestionResolved || refreshWorkout,
                    workoutExerciseId: we._id,
                    invalidateSets: async (targetId) => {
                      const key = ['workoutSets', targetId || we._id];
                      await queryClient.cancelQueries(key, { exact: true });
                      return queryClient.invalidateQueries(key, { exact: true });
                    },
                    resolution,
                  })}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Table with zipped sets */}
      <div className="superset-sets-container">
        <table className="superset-table">
          <colgroup>
            <col className="superset-round-column" />
            <col className="superset-exercise-column" />
            {setColumns.map(col => <col key={col.field} />)}
            {editable && <col />}
          </colgroup>
          <thead>
            <tr>
              <th className="superset-round-header" aria-label="Round"><span className="superset-header-full">Round</span><span className="superset-header-short">#</span></th>
              <th className="superset-exercise-header" aria-label="Exercise"><span className="superset-header-full">Exercise</span><span className="superset-header-short">Ex</span></th>
              {/* Use first exercise's columns as reference */}
              {setColumns.map(col => (
                <th key={col.field} aria-label={col.header} className={`superset-set-header superset-set-${col.type}`} data-unit-kind={col.field === 'weight' || col.field === 'distance' ? col.field : undefined}>
                  <span className="superset-header-full">{col.header}</span>
                  <span className="superset-header-short">{col.field === 'weight' ? `Wt (${weightUnit})` : col.field === 'distance' ? `Dist (${distanceUnit})` : col.header}</span>
                </th>
              ))}
              {editable && (
                <th className="superset-set-header superset-set-action"></th>
              )}
            </tr>
          </thead>
          <tbody>
            {zippedRows.map((row) => {
              const { key, setIndex, exerciseIndex, workoutExercise, exercise, set, columns, colors, isFirstInRound, isLastInRound, isCurrentSet, isSetInteractive } = row;
              const isRecordRow = recordExerciseIds.has(workoutExercise._id);
              
              return (
                <tr 
                  key={key}
                  data-shortcut-exercise-id={workoutExercise._id}
                  data-shortcut-set-index={set ? setIndex : undefined}
                  className={`superset-set-row ${isFirstInRound ? 'round-start' : ''} ${isLastInRound ? 'round-end' : ''} ${isRecordRow ? 'is-record' : ''}${newSetRows.className(workoutExercise._id, set?._id)}`}
                  style={{ 
                    backgroundColor: colors.bg,
                    borderLeftColor: isRecordRow ? 'rgba(255, 215, 0, 0.85)' : colors.border
                  }}
                >
                  {/* Round number - only show on first exercise of each round */}
                  {isFirstInRound && (
                    <td className="superset-round-cell" rowSpan={workoutExercises.length}>
                      <span className="superset-round-number">{setIndex + 1}</span>
                    </td>
                  )}
                  
                  {/* Exercise letter indicator */}
                  <td className="superset-exercise-cell">
                    <span 
                      className="superset-exercise-indicator"
                      style={{ backgroundColor: colors.border }}
                    >
                      {String.fromCharCode(65 + exerciseIndex)}
                    </span>
                  </td>
                  
                  {/* Set values */}
                  {columns.map(col => (
                    <td key={col.field} data-field={col.field} className={`superset-set-cell superset-set-${col.type}${coachFieldEffectClass(displayedCoachFieldEffects, workoutExercise._id, set?._id, col.field)}`} data-unit-kind={col.field === 'weight' || col.field === 'distance' ? col.field : undefined}>
                      {set ? (
                        col.type === 'checkbox' ? (
                          <div className="set-checkbox-wrapper">
                          {completionEditable ? (
                            <input
                              type="checkbox"
                              className={`${isCurrentSet ? 'current-set-checkbox' : ''} ${!isSetInteractive ? 'set-checkbox-readonly' : ''}`}
                              checked={set[col.field] || false}
                              onChange={(e) => {
                                if (!isSetInteractive) return;
                                // Read the round-major order before optimistic completion.
                                const firstUnchecked = zippedRows.find(candidate => candidate.set && !candidate.set.complete);
                                if (e.target.checked && firstUnchecked?.key === key && setIndex < maxSets - 1) {
                                  scrollDownBySetRow(e.currentTarget);
                                }
                                handleSetChange(workoutExercise._id, set._id, { [col.field]: e.target.checked })
                              }}
                              disabled={isAddingSet}
                            />
                          ) : set[col.field] ? (
                            <CheckIcon className="checkmark" />
                          ) : isWorkoutEditable(workout) ? (
                            <span>-</span>
                          ) : (
                            <XMarkIcon className="cross" />
                          )}
                          {set.complete && (() => {
                            const d = parseSetDate(set.completed_at || workout.completed_at);
                            if (!d) return null;
                            const label = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                            if (!completionEditable || structureLocked || isSelectionMode) {
                              return <span className="set-completed-at-label" title="Completion time">{label}</span>;
                            }
                            const localIso = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
                            const owner = timeScopeRef.current;
                            return <span className="set-completed-at-label" title="Tap to adjust completion time"
                              onClick={(e) => {
                                const input = e.currentTarget.querySelector('input');
                                if (input) { input.value = localIso; input.showPicker?.(); }
                              }}>
                              {label}
                              <input id={`set-time-input-${set._id}`} type="datetime-local" className="set-completed-at-input"
                                defaultValue={localIso}
                                onChange={(e) => {
                                  if (e.target.value && e.target.value !== localIso) {
                                    saveSetTime(workoutExercise._id, set._id, e.target.value, owner);
                                  }
                                }} />
                            </span>;
                          })()}
                          </div>
                        ) : col.type === 'duration' ? (
                          <DurationInput
                            value={set[col.field]}
                            min={col.min}
                            step={col.step}
                            onChange={(value) => handleSetChange(workoutExercise._id, set._id, { [col.field]: value })}
                            onAccept={(value) => handleSetChange(
                              workoutExercise._id,
                              set._id,
                              structureLocked ? { complete: true } : { [col.field]: value, complete: true }
                            )}
                            disabled={isAddingSet}
                            editable={editable}
                            modalService={modalService}
                            enableTimer={!isTemplateMode && workout.status === 'ongoing' && isCurrentSet}
                            allowTimerWhenReadOnly={structureLocked && completionEditable}
                            isCurrentSet={isCurrentSet}
                            imageSrc={imageSrcMap[workoutExercise._id]}
                            title={exercise?.name || ''}
                          />
                        ) : col.field === 'weight' ? (
                          <Input
                              preserveFocusedDraft
                            value={formatWeight(weightUnit === 'kg' ? lbsToKg(set[col.field]) : set[col.field])}
                            min={col.min}
                            step={col.step}
                            type={col.type}
                            onChange={(value) => {
                              const toStore = weightUnit === 'kg' ? kgToLbs(value) : value;
                              handleSetChange(workoutExercise._id, set._id, { [col.field]: toStore });
                            }}
                            disabled={isAddingSet}
                            editable={editable}
                            clearWorkoutCoachOnFocus
                            showPlateCalculator={true}
                            plateStorageKey={set._id}
                          />
                        ) : col.field === 'distance' ? (
                          <Input
                              preserveFocusedDraft
                            value={distanceUnit === 'mi' ? kmToMiles(set[col.field]) : set[col.field]}
                            min={col.min}
                            step={col.step}
                            type={col.type}
                            onChange={(value) => {
                              const toStore = distanceUnit === 'mi' ? milesToKm(value) : value;
                              handleSetChange(workoutExercise._id, set._id, { [col.field]: toStore });
                            }}
                            disabled={isAddingSet}
                            editable={editable}
                            clearWorkoutCoachOnFocus
                          />
                        ) : (
                          <Input
                              preserveFocusedDraft
                            value={set[col.field]}
                            min={col.min}
                            step={col.step}
                            type={col.type}
                            onChange={(value) => handleSetChange(workoutExercise._id, set._id, { [col.field]: value })}
                            disabled={isAddingSet}
                            editable={editable}
                            clearWorkoutCoachOnFocus
                          />
                        )
                      ) : (
                        <span className="superset-empty-cell">-</span>
                      )}
                    </td>
                  ))}

                  {/* Delete button on right - only show on first exercise of each round, spans all rows */}
                  {editable && isFirstInRound && (
                    <td className="superset-action-cell" rowSpan={workoutExercises.length}>
                      <button
                        className="trash-button"
                        onClick={() => handleRemoveSetFromAll(setIndex)}
                        title="Remove this round from all exercises"
                      >
                        <TrashIcon />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
            
            {/* Add set row */}
            {editable && (
              <tr className="superset-add-row">
                <td colSpan={2 + setColumns.length + (editable ? 1 : 0)}>
                  <button
                    className="superset-add-round-btn"
                    onClick={handleAddSetToAll}
                    disabled={isAddingSet}
                  >
                    <PlusIcon style={{ width: 16, height: 16 }} />
                    Add Round
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
    </>
  );
}

export default SupersetExercise;
